import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, Coffee, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { xboxApiService } from '@/services/xboxApi';
import { useToast } from '@/components/ui/toast';

const Presence: React.FC = () => {
  const [currentPresence, setCurrentPresence] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const { toast, Toaster } = useToast();

  // Fetch current presence on mount
  useEffect(() => {
    fetchCurrentPresence();
  }, []);

  const fetchCurrentPresence = async () => {
    setIsLoading(true);
    try {
      const res = await xboxApiService.getCurrentPresence();
      if (res.data && !res.error) {
        setCurrentPresence(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch presence:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetOnline = async () => {
    setActionInProgress('online');
    try {
      // Setting presence with any title ID marks you as online
      const res = await xboxApiService.setPresence('328178078', 'active');
      if (res.error) {
        toast(`Failed to go online: ${res.error}`, 'error');
      } else {
        toast('You are now appearing Online', 'success');
        // Wait a moment then refresh
        setTimeout(fetchCurrentPresence, 1500);
      }
    } catch (error) {
      toast('Network error', 'error');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleSetAway = async () => {
    setActionInProgress('away');
    try {
      const res = await xboxApiService.setPresence('328178078', 'inactive');
      if (res.error) {
        toast(`Failed to set Away: ${res.error}`, 'error');
      } else {
        toast('You are now appearing Away', 'success');
        setTimeout(fetchCurrentPresence, 1500);
      }
    } catch (error) {
      toast('Network error', 'error');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleGoOffline = async () => {
    setActionInProgress('offline');
    try {
      const res = await xboxApiService.clearPresence();
      if (res.error) {
        toast(`Failed to go offline: ${res.error}`, 'error');
      } else {
        toast('You are now appearing Offline', 'success');
        setTimeout(fetchCurrentPresence, 1500);
      }
    } catch (error) {
      toast('Network error', 'error');
    } finally {
      setActionInProgress(null);
    }
  };

  // Determine current state
  const getPresenceState = () => {
    if (!currentPresence) return 'Unknown';
    return currentPresence.state || 'Unknown';
  };

  const presenceState = getPresenceState();

  // Get presence info
  const getPresenceInfo = () => {
    if (!currentPresence) return null;
    
    const info: { device?: string; title?: string; lastSeen?: string } = {};
    
    // Check devices for current activity
    if (currentPresence.devices && currentPresence.devices.length > 0) {
      const device = currentPresence.devices[0];
      info.device = device.type;
      if (device.titles && device.titles.length > 0) {
        const activeTitle = device.titles.find((t: any) => t.state === 'Active') || device.titles[0];
        info.title = activeTitle.name;
      }
    }
    
    // Last seen info
    if (currentPresence.lastSeen) {
      info.lastSeen = currentPresence.lastSeen.timestamp;
    }
    
    return info;
  };

  const presenceInfo = getPresenceInfo();

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      <Toaster />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Presence Control</h1>
          <p className="text-muted-foreground mt-2">
            Control your online status visibility
          </p>
        </div>
        
        <Button
          onClick={fetchCurrentPresence}
          disabled={isLoading}
          variant="outline"
          className="flex w-full items-center justify-center gap-2 sm:w-auto"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Current Status
          </CardTitle>
          <CardDescription>How you appear to others on Xbox Live</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-3 p-4 rounded-lg flex-1 border ${
              presenceState === 'Online' 
                ? 'bg-green-500/10 border-green-500/20' 
                : presenceState === 'Away'
                ? 'bg-yellow-500/10 border-yellow-500/20'
                : 'bg-muted/50 border-border'
            }`}>
              {presenceState === 'Online' ? (
                <Wifi className="h-8 w-8 text-green-500" />
              ) : presenceState === 'Away' ? (
                <Coffee className="h-8 w-8 text-yellow-500" />
              ) : (
                <WifiOff className="h-8 w-8 text-muted-foreground" />
              )}
              <div className="flex-1">
                <div className={`font-semibold text-lg ${
                  presenceState === 'Online' 
                    ? 'text-green-500' 
                    : presenceState === 'Away'
                    ? 'text-yellow-500'
                    : 'text-muted-foreground'
                }`}>
                  {presenceState}
                </div>
                {presenceInfo?.device && (
                  <div className="text-sm text-muted-foreground">
                    Device: {presenceInfo.device}
                  </div>
                )}
                {presenceInfo?.title && (
                  <div className="text-sm text-muted-foreground">
                    {presenceInfo.title}
                  </div>
                )}
                {presenceState === 'Offline' && presenceInfo?.lastSeen && (
                  <div className="text-sm text-muted-foreground">
                    Last seen: {formatDate(presenceInfo.lastSeen)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Change Status</CardTitle>
          <CardDescription>Select how you want to appear to others</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {/* Online */}
            <button
              onClick={handleSetOnline}
              disabled={!!actionInProgress}
              className={`flex flex-col items-center gap-3 p-6 rounded-lg border transition-all ${
                presenceState === 'Online'
                  ? 'border-green-500 bg-green-500/10'
                  : 'border-border hover:border-green-500/50 hover:bg-green-500/5'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {actionInProgress === 'online' ? (
                <RefreshCw className="h-10 w-10 text-green-500 animate-spin" />
              ) : (
                <Wifi className={`h-10 w-10 ${presenceState === 'Online' ? 'text-green-500' : 'text-muted-foreground'}`} />
              )}
              <div className="text-center">
                <div className={`font-semibold ${presenceState === 'Online' ? 'text-green-500' : 'text-foreground'}`}>
                  Online
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Appear available
                </div>
              </div>
            </button>

            {/* Away */}
            <button
              onClick={handleSetAway}
              disabled={!!actionInProgress}
              className={`flex flex-col items-center gap-3 p-6 rounded-lg border transition-all ${
                presenceState === 'Away'
                  ? 'border-yellow-500 bg-yellow-500/10'
                  : 'border-border hover:border-yellow-500/50 hover:bg-yellow-500/5'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {actionInProgress === 'away' ? (
                <RefreshCw className="h-10 w-10 text-yellow-500 animate-spin" />
              ) : (
                <Coffee className={`h-10 w-10 ${presenceState === 'Away' ? 'text-yellow-500' : 'text-muted-foreground'}`} />
              )}
              <div className="text-center">
                <div className={`font-semibold ${presenceState === 'Away' ? 'text-yellow-500' : 'text-foreground'}`}>
                  Away
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Appear idle
                </div>
              </div>
            </button>

            {/* Offline */}
            <button
              onClick={handleGoOffline}
              disabled={!!actionInProgress}
              className={`flex flex-col items-center gap-3 p-6 rounded-lg border transition-all ${
                presenceState === 'Offline'
                  ? 'border-gray-500 bg-gray-500/10'
                  : 'border-border hover:border-gray-500/50 hover:bg-gray-500/5'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {actionInProgress === 'offline' ? (
                <RefreshCw className="h-10 w-10 text-gray-500 animate-spin" />
              ) : (
                <EyeOff className={`h-10 w-10 ${presenceState === 'Offline' ? 'text-gray-400' : 'text-muted-foreground'}`} />
              )}
              <div className="text-center">
                <div className={`font-semibold ${presenceState === 'Offline' ? 'text-gray-400' : 'text-foreground'}`}>
                  Offline
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Appear invisible
                </div>
              </div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card>
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <Wifi className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong className="text-foreground">Online</strong> - Friends can see you're active and available</span>
            </li>
            <li className="flex items-start gap-2">
              <Coffee className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
              <span><strong className="text-foreground">Away</strong> - Friends see you as idle/away from controller</span>
            </li>
            <li className="flex items-start gap-2">
              <EyeOff className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <span><strong className="text-foreground">Offline</strong> - Appear completely offline to everyone</span>
            </li>
            <li className="flex items-start gap-2 mt-4 pt-2 border-t border-border">
              <span className="text-foreground">•</span>
              <span>Status updates within a few seconds on Xbox Live</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-foreground">•</span>
              <span>Playing a game on console may override this setting</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default Presence;
